/**
 * Random access into a fragmented MP4 proxy: mp4mux ends it with an `mfra`
 * box whose `tfra` entries give each fragment's time and byte offset.
 *
 * @module
 */

export interface FragmentIndex {
  /** Bytes before the first fragment: ftyp and moov, appended before any fragment. */
  initBytes: number;
  /** Where the fragments stop (the mfra box). */
  endBytes: number;
  /** Fragment start times (s, proxy time) and byte offsets, ascending. */
  fragments: { t: number; offset: number }[];
}

interface Box {
  type: string;
  start: number;
  /** Offset of the payload. */
  body: number;
  end: number;
}

function boxes(view: DataView, from: number, to: number): Box[] {
  const out: Box[] = [];
  let at = from;
  while (at + 8 <= to) {
    let size = view.getUint32(at);
    const type = String.fromCharCode(
      view.getUint8(at + 4),
      view.getUint8(at + 5),
      view.getUint8(at + 6),
      view.getUint8(at + 7),
    );
    let body = at + 8;
    if (size === 1) {
      if (at + 16 > to) break;
      size = Number(view.getBigUint64(at + 8));
      body = at + 16;
    } else if (size === 0) {
      size = to - at;
    }
    if (size < body - at || at + size > to) break;
    out.push({ type, start: at, body, end: at + size });
    at += size;
  }
  return out;
}

const child = (view: DataView, b: Box | undefined, type: string) =>
  b ? boxes(view, b.body, b.end).find((c) => c.type === type) : undefined;

/** Track id → timescale, from the moov box in `init`. */
export function timescales(init: ArrayBuffer): Map<number, number> {
  const view = new DataView(init);
  const out = new Map<number, number>();
  const moov = boxes(view, 0, init.byteLength).find((b) => b.type === "moov");
  if (!moov) return out;
  for (const trak of boxes(view, moov.body, moov.end).filter((b) => b.type === "trak")) {
    const tkhd = child(view, trak, "tkhd");
    const mdhd = child(view, child(view, trak, "mdia"), "mdhd");
    if (!tkhd || !mdhd) continue;
    const id = view.getUint32(tkhd.body + (view.getUint8(tkhd.body) === 1 ? 20 : 12));
    const scale = view.getUint32(mdhd.body + (view.getUint8(mdhd.body) === 1 ? 20 : 12));
    if (scale > 0) out.set(id, scale);
  }
  return out;
}

/** The `mfra` box's size, from the `mfro` box in the file's last 16 bytes. */
export function mfraSize(tail: ArrayBuffer): number | null {
  if (tail.byteLength < 16) return null;
  const view = new DataView(tail, tail.byteLength - 16);
  const type = String.fromCharCode(view.getUint8(4), view.getUint8(5), view.getUint8(6), view.getUint8(7));
  return type === "mfro" ? view.getUint32(12) : null;
}

/** The first `tfra` of an `mfra` box: its track and each fragment's time (in track ticks) and offset. */
export function parseMfra(mfra: ArrayBuffer): { trackId: number; entries: { ticks: number; offset: number }[] } | null {
  const view = new DataView(mfra);
  const top = boxes(view, 0, mfra.byteLength).find((b) => b.type === "mfra");
  const tfra = top && boxes(view, top.body, top.end).find((b) => b.type === "tfra");
  if (!tfra || tfra.body + 16 > tfra.end) return null;
  const v1 = view.getUint8(tfra.body) === 1;
  const sizes = view.getUint32(tfra.body + 8);
  const skip = ((sizes >> 4) & 3) + ((sizes >> 2) & 3) + (sizes & 3) + 3;
  const count = view.getUint32(tfra.body + 12);
  const entries: { ticks: number; offset: number }[] = [];
  let at = tfra.body + 16;
  for (let i = 0; i < count && at + (v1 ? 16 : 8) + skip <= tfra.end; i++) {
    const ticks = v1 ? Number(view.getBigUint64(at)) : view.getUint32(at);
    const offset = v1 ? Number(view.getBigUint64(at + 8)) : view.getUint32(at + 4);
    entries.push({ ticks, offset });
    at += (v1 ? 16 : 8) + skip;
  }
  // mp4mux adds an entry at offset 0, timed at the end, to audio-only files.
  const fragments = entries.filter((e) => e.offset > 0).sort((x, y) => x.offset - y.offset);
  return { trackId: view.getUint32(tfra.body + 4), entries: fragments };
}

/** The offset of the last fragment starting at or before `t` (proxy seconds). */
export function fragmentAt(index: FragmentIndex, t: number): number {
  let best = index.initBytes;
  for (const f of index.fragments) {
    if (f.t > t) break;
    best = f.offset;
  }
  return best;
}
